import { createSlice } from "@reduxjs/toolkit"
import { etlDataReducerInit, EtlDataState } from "./etlData.types"
import { loadUsers } from "../useCases/load-users/loadUsers.use-case"
import { loadBrands } from "../useCases/load-brands/loadBrands.use-case"
import { loadSuppliers } from "../useCases/load-suppliers/loadSuppliers.use-case"
import { loadCategories } from "../useCases/load-categories/loadCategories.use-case"
import { loadProducts } from "../useCases/load-products/loadProducts.use-case"
import { loadScheduleTypes } from "../useCases/load-schedule-types/loadScheduleTypes.use-case"
import { patchBrandMapping } from "../../formStep/useCases/patch-brands-mapping/patchBrandsMapping.use-case"
import { patchSupplierMapping } from "../../formStep/useCases/patch-suppliers-mapping/patchSuppliersMapping.use-case"
import { patchCategoryMapping } from "../../formStep/useCases/patch-categories-mapping/patchCategoriesMapping.use-case"
import { patchUserMapping } from "../../formStep/useCases/patch-users-mapping/patchUsersMapping.use-case"
import { patchProductMapping } from "../../formStep/useCases/patch-products-mapping/patchProductsMapping.use-case"
import { patchScheduleTypeMapping } from "../../formStep/useCases/patch-schedule-types-mapping/patchScheduleTypesMapping.use-case"
import { createProduct } from "../../formStep/useCases/create-product/createProduct.use-case"
import { loadEtlImports } from "../useCases/load-imports/loadEtlImports.use-case"
import { initApp } from "../../app/useCases/init-app/initApp.use-case"
import { LoadingStatusEnum } from "etl/types"
import { loadLaboratories } from "../useCases/load-laboratories/loadLaboratories.use-case"
import { patchLaboratoryMapping } from "../../formStep/useCases/patch-laboratories-mapping/patchLaboratoriesMapping.use-case"

export const ETL_DATA_FEATURE_KEY = "etlData"

export const initialEtlDataState: EtlDataState = etlDataReducerInit()

const etlDataSlice = createSlice({
	name: ETL_DATA_FEATURE_KEY,
	initialState: initialEtlDataState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(initApp.fulfilled, (state, action) => {
				state.appIsInitialized = true;
				state.selectedEtlImportUuid = action.payload.selectedEtlImportUuid;
			})
			// Import
			.addCase(loadEtlImports.fulfilled, (state, action) => {
				state.etlImports = action.payload.etlImports;
				state.isLoadingImports = false;
			})
			.addCase(loadEtlImports.pending, (state) => {
				state.isLoadingImports = true;
			})
			.addCase(loadEtlImports.rejected, (state) => {
				state.isLoadingImports = false;
			})
			// Users
			.addCase(loadUsers.fulfilled, (state, action) => {
				state.extractUsers = action.payload.extractUsers;
				state.dictionaryUsers = action.payload.dictionnaryUsers;
				state.usersLoadingStatus = LoadingStatusEnum.Loaded;
			})
			.addCase(loadUsers.pending, (state) => {
				state.usersLoadingStatus = LoadingStatusEnum.Loading;
			})
			.addCase(loadUsers.rejected, (state) => {
				state.usersLoadingStatus = LoadingStatusEnum.Error;
			})
			.addCase(patchUserMapping.fulfilled, (state, action) => {
				state.dictionaryUsers = action.payload.dictionaryUsers;
			})
			// Brands
			.addCase(loadBrands.fulfilled, (state, action) => {
				state.extractBrands = action.payload.extractBrands;
				state.dictionaryBrands = action.payload.dictionaryBrands;
				state.brandsLoadingStatus = LoadingStatusEnum.Loaded;
			})
			.addCase(loadBrands.pending, (state) => {
				state.brandsLoadingStatus = LoadingStatusEnum.Loading;
			})
			.addCase(loadBrands.rejected, (state) => {
				state.brandsLoadingStatus = LoadingStatusEnum.Error;
			})
			.addCase(patchBrandMapping.fulfilled, (state, action) => {
				state.dictionaryBrands = action.payload.dictionaryBrands;
			})
			// Suppliers
			.addCase(loadSuppliers.fulfilled, (state, action) => {
				state.dictionarySuppliers = action.payload.dictionarySuppliers;
				state.extractSuppliers = action.payload.extractSuppliers;
				state.suppliersLoadingStatus = LoadingStatusEnum.Loaded;
			})
			.addCase(loadSuppliers.pending, (state) => {
				state.suppliersLoadingStatus = LoadingStatusEnum.Loading;
			})
			.addCase(loadSuppliers.rejected, (state) => {
				state.suppliersLoadingStatus = LoadingStatusEnum.Error;
			})
			.addCase(patchSupplierMapping.fulfilled, (state, action) => {
				state.dictionarySuppliers = action.payload.dictionarySuppliers;
			})
			// Categories
			.addCase(loadCategories.fulfilled, (state, action) => {
				state.dictionaryCategories = action.payload.dictionnaryCategories;
				state.extractCategories = action.payload.extractCategories;
				state.categoriesLoadingStatus = LoadingStatusEnum.Loaded;
			})
			.addCase(loadCategories.pending, (state) => {
				state.categoriesLoadingStatus = LoadingStatusEnum.Loading;
			})
			.addCase(loadCategories.rejected, (state) => {
				state.categoriesLoadingStatus = LoadingStatusEnum.Error;
			})
			.addCase(patchCategoryMapping.fulfilled, (state, action) => {
				state.dictionaryCategories = action.payload.dictionaryCategories;
			})
			// Products
			.addCase(loadProducts.fulfilled, (state, action) => {
				state.dictionaryProducts = action.payload.dictionaryProducts;
				state.extractProducts = action.payload.extractProducts;
				state.productsLoadingStatus = LoadingStatusEnum.Loaded;
			})
			.addCase(loadProducts.pending, (state) => {
				state.productsLoadingStatus = LoadingStatusEnum.Loading;
			})
			.addCase(loadProducts.rejected, (state) => {
				state.productsLoadingStatus = LoadingStatusEnum.Error;
			})
			.addCase(patchProductMapping.fulfilled, (state, action) => {
				state.dictionaryProducts = action.payload.dictionaryProducts;
			})
			.addCase(createProduct.fulfilled, (state, action) => {
				state.extractProducts = [...state.extractProducts, action.payload.newProduct];
			})
			// Laboratories
			.addCase(loadLaboratories.fulfilled, (state, action) => {
				state.dictionaryLaboratories = action.payload.dictionaryLaboratories;
				state.extractLaboratories = action.payload.extractLaboratories;
				state.laboratoriesLoadingStatus = LoadingStatusEnum.Loaded;
			})
			.addCase(loadLaboratories.pending, (state) => {
				state.laboratoriesLoadingStatus = LoadingStatusEnum.Loading;
			})
			.addCase(loadLaboratories.rejected, (state) => {
				state.laboratoriesLoadingStatus = LoadingStatusEnum.Error;
			})
			.addCase(patchLaboratoryMapping.fulfilled, (state, action) => {
				state.dictionaryLaboratories = action.payload.dictionaryLaboratories;
			})
			// ScheduleTypes
			.addCase(loadScheduleTypes.fulfilled, (state, action) => {
				state.dictionaryScheduleTypes = action.payload.dictionaryScheduleTypes;
				state.extractScheduleTypes = action.payload.extractScheduleTypes;
				state.scheduleTypesLoadingStatus = LoadingStatusEnum.Loaded;
			})
			.addCase(loadScheduleTypes.pending, (state) => {
				state.scheduleTypesLoadingStatus = LoadingStatusEnum.Loading
			})
			.addCase(loadScheduleTypes.rejected, (state) => {
				state.scheduleTypesLoadingStatus = LoadingStatusEnum.Error;
			})
			.addCase(patchScheduleTypeMapping.fulfilled, (state, action) => {
				state.dictionaryScheduleTypes = action.payload.dictionaryScheduleTypes;
			})
	},
})

export const etlData = etlDataSlice.reducer;

export const etlDataActions = etlDataSlice.actions;
